<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <customer-information
            v-model:editing="editingCustomerInformation"
            :customer="primaryCustomer"
            :facility="facility"
            show-errors
        />

        <checkout-section-separator />

        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            title="Billing Address"
        />

        <checkout-section-separator />

        <recurring-information
            v-if="requiresRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
        />

        <document-section
            :contract="contract"
            :is-signing-document="isValidatingRecurring || isPending"
            balance-due-mode
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="submitCount > 0 && Object.keys(errors).length > 0"
            class="text-red-600 mt-4 font-medium"
        >
            Please fix the errors in red above.
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type {
    CheckoutRecurringInput,
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { ref, toRef } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { useKioskSuccess } from '~/composables/use-kiosk-success'
import { useActiveFacility, useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import CustomerInformation, {
    CustomerInformationCheckoutFormInitialValues,
    CustomerInformationCheckoutFormSchema
} from '~/components/Checkout/Sections/CustomerInformation.vue'
import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { GraphQLRequestError } from 'awesome-graphql-client'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import { checkoutRecurringInput } from '~/components/Checkout/Forms/checkout-helpers'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract?: FragmentType<typeof DocumentSectionContractFragment>
}>()

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,

    editingCustomerInformation,
    editingBillingAddress,
    editingRecurringInformation,

    recurringTotal,
    requiresRecurringToken,

    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        customer: CustomerInformationCheckoutFormSchema,
        address: BillingAddressSchema,
        recurring: RecurringInformationSchema
    })
)

const activeFacility = useActiveFacility()
const { handleSubmit, errors, submitCount, validateField } = useForm({
    validationSchema: schema,
    initialValues: {
        customer: CustomerInformationCheckoutFormInitialValues(
            primaryCustomer.value,
            facility.value,
            activeFacility.value.organization.onlineGuestId
        ),
        address: BillingAddressInitialValues(
            primaryCustomer.value,
            fullAddressRequired.value
        ),
        recurring: RecurringInformationInitialValues(
            requiresRecurringToken.value ? recurringTotal.value : null
        )
    }
})

const isValidatingRecurring = ref(false)
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()

async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    // Recurring checkout
    try {
        isValidatingRecurring.value = true

        // If recurring field is not valid, submit stripe form
        // We use silent so we don't show the error message until after stripe form is submitted
        const { valid: validRecurring } = await validateField('recurring', {
            mode: 'silent'
        })
        if (!validRecurring) {
            await recurringInformationRef.value.submitRecurringForm()
        }

        return onSubmit(event)
    } finally {
        isValidatingRecurring.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const query = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await query(
            graphql(/** GraphQL */ `
                mutation InlineSubmitContractCustomer(
                    $input: InlineSubmitContractInput!
                ) {
                    inlineSubmitContract(input: $input) {
                        __typename
                        ... on InlineSubmitContractResult {
                            contract {
                                __typename
                                ... on PlanContract {
                                    referenceNumber
                                    planType
                                    customer {
                                        firstName
                                    }
                                }
                            }
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.inlineSubmitContract === null) {
            throw new Error(
                'Oops! Something went wrong with the checkout. Don’t worry, it happens sometimes. Please try again, and if the issue continues, contact your facility for help! (Code: CFKCBDM)'
            )
        }

        if (
            response.inlineSubmitContract.__typename ===
                'InlineSubmitContractResult' ||
            response.inlineSubmitContract.__typename === 'ValidationError'
        ) {
            return response.inlineSubmitContract
        }

        throw new Error()
    }
})

const kioskSuccessFunction = useKioskSuccess()

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const savedDocumentInput = ref()
const onSubmit = handleSubmit(values => {
    // Customer and Address
    const customer = {
        ...values.customer,
        dateOfBirth: null,
        mobilePhone: values.customer.mobilePhone.number,
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Add date of birth if it exists
    if (values.customer.dateOfBirth !== '') {
        customer.dateOfBirth = values.customer.dateOfBirth
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    incrementProcessing()

    mutate(
        {
            checkoutId: checkoutId.value,
            customer,
            recurring,
            document: savedDocumentInput.value
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert('Heads Up', messageBuilder)
                    }
                } else {
                    kioskSuccessFunction({
                        includedInvoice: false,
                        wasGuest: false,
                        data: {
                            referenceNumber: data.contract.referenceNumber,
                            planType: data.contract.planType,
                            firstName: data.contract.customer.firstName
                        }
                    })
                }
            },
            onError(error) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert('Heads Up', String(error))
                }
            },
            onSettled() {
                decrementProcessing()
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    // Save the document
    savedDocumentInput.value = documentInput
    // The handle the submit event
    handleSubmitCheckoutForm()
}
</script>
